import { type Transaction } from "@tiptap/pm/state";
import { sortBy } from "es-toolkit";

import { mergeInlineEditables } from "@/components/input/utils/mergeInlineEditables";
import { jumpToNearbySiblingNode } from "@/components/tiptap/keyboard-handler/KeyboardHandler";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { createLogger } from "@/utils/logger";

import { nodeIsExpression } from "./utils";

import type { DocumentModel } from "@/models/DocumentModel";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { Range } from "@tiptap/core";
import type { Node as ProsemirrorNode } from "@tiptap/pm/model";

const logger = createLogger("TiptapExpression");

type MergeNodePair = { pos: number; leftNode: InlineNodeModel; rightNode: InlineNodeModel };

/**
 * Whether the two expressions were siblings separated by content (the text between them) in
 * the doc before this transaction. Distinguishes "the text between two same-line maths was
 * removed" (→ merge immediately) from a structural join, e.g. Backspace joining two
 * paragraphs, where they were in different blocks (→ keep deferring the merge).
 */
function hadContentBetween(before: ProsemirrorNode, leftId: string, rightId: string): boolean {
  let left: { parent: ProsemirrorNode | null; index: number } | undefined;
  let right: { parent: ProsemirrorNode | null; index: number } | undefined;
  before.descendants((node, _pos, parent, index) => {
    const id = node.attrs?.nodeId as string | undefined;
    if (id === leftId) left = { parent, index };
    else if (id === rightId) right = { parent, index };
  });
  return !!left && !!right && left.parent === right.parent && right.index - left.index > 1;
}

function findExpressionNodesToMerge(transaction: Transaction, doc: DocumentModel, range?: Range) {
  const nodesToMerge: MergeNodePair[] = [];
  const isOutsideExpression = !doc.cursor.isActive;
  const { selection } = doc.editor.tiptap?.state ?? {};
  const currentHead = selection?.head ?? 0;
  const isCollapsedCursor = selection?.from === selection?.to;
  const removedContent = transaction.before.content.size > transaction.doc.content.size;

  const collectMergePairs = (
    node: ProsemirrorNode,
    pos: number,
    parent: ProsemirrorNode | null,
    index: number,
  ) => {
    const nextIndex = index + 1;
    const nextNode = parent && nextIndex < parent.childCount ? parent.child(nextIndex) : undefined;

    if (!nextNode || !nodeIsExpression(node) || !nodeIsExpression(nextNode)) {
      return;
    }

    const leftWrapperNode = doc.nodes.get(node.attrs.nodeId) as WrapperNodeModel | undefined;
    const leftInlineNode = leftWrapperNode?.inlineNode;

    const rightWrapperNode = doc.nodes.get(nextNode.attrs.nodeId) as WrapperNodeModel | undefined;
    const rightInlineNode = rightWrapperNode?.inlineNode;

    if (!leftInlineNode || !rightInlineNode) return;

    const from = pos + 1;
    const cursorIsBetween = isOutsideExpression && isCollapsedCursor && currentHead === from;

    // Keep two adjacent expressions apart while the cursor is parked between them (e.g.
    // after splitting one with a space, or just after a structural join). But when this
    // transaction removed the text that was between them, merge immediately, wherever the
    // cursor landed.
    const removedTextBetween =
      removedContent &&
      hadContentBetween(
        transaction.before,
        String(node.attrs.nodeId),
        String(nextNode.attrs.nodeId),
      );
    if (!cursorIsBetween || removedTextBetween) {
      nodesToMerge.push({ pos, leftNode: leftInlineNode, rightNode: rightInlineNode });
    }
  };

  if (range) {
    transaction.doc.content.nodesBetween(range.from, range.to, collectMergePairs);
  } else {
    transaction.doc.content.descendants(collectMergePairs);
  }

  return nodesToMerge;
}

export function mergeExpressionNodes(
  nodesToMerge: MergeNodePair[],
  doc: DocumentModel,
  transaction?: Transaction,
) {
  const { tiptap } = doc.editor;
  if (!tiptap) return -1;

  let focusPos = -1;
  let moveToNode: [InlineNodeModel, number] | null = null;
  const isPasteTransaction = !!transaction?.getMeta("paste");

  const isOutsideExpression = !doc.cursor.isActive;
  const rangesToDelete: Array<{ from: number; to: number }> = [];

  for (const { pos, leftNode, rightNode } of sortBy(nodesToMerge, [(p) => -p.pos])) {
    const from = pos + 1;
    const to = from + 1;

    const currentHead = tiptap.state.selection.head;
    const cursorIsBetween = isOutsideExpression && currentHead === from;
    const cursorIsAfter = isOutsideExpression && currentHead === to && !isPasteTransaction;

    /**
     * Compute target index within the merged leftNode.
     *
     * Processed right-to-left: a prior iteration may have already set moveToNode to rightNode,
     * which needs to be translated into leftNode's index space the same way as a live cursor in rightNode.
     */
    let targetIndex: number | null = null;

    if (moveToNode?.[0] === rightNode) {
      targetIndex = leftNode.nodes.length + moveToNode[1];
    } else if (doc.cursor.isActive && doc.cursor.node === rightNode) {
      targetIndex = leftNode.nodes.length + (doc.cursor.index ?? 0);
    } else if (doc.cursor.isActive && doc.cursor.node === leftNode) {
      targetIndex = doc.cursor.index ?? 0;
    } else if (cursorIsBetween || cursorIsAfter) {
      targetIndex = leftNode.nodes.length;
    }

    if (targetIndex !== null) {
      moveToNode = [leftNode, targetIndex];
    }

    const boundaryLeft = leftNode.nodes[leftNode.nodes.length - 1];
    const boundaryRight = rightNode.nodes[0];

    leftNode.appendChildren(rightNode.nodes);
    rangesToDelete.push({ from, to });

    if (doc.cursor.node === rightNode) {
      focusPos = pos;
    }

    const cursorInRightNode = doc.cursor.node === rightNode;

    if (boundaryLeft && boundaryRight && cursorInRightNode) {
      const lengthBefore = leftNode.nodes.length;

      mergeInlineEditables(doc, boundaryLeft, boundaryRight, { skipCursorUpdate: true });

      const merged = leftNode.nodes.length < lengthBefore;
      if (merged && moveToNode?.[0] === leftNode && moveToNode[1] > 0) {
        moveToNode = [leftNode, moveToNode[1] - 1];
      }
    }

    if (leftNode.parent?.is(WrapperNodeModel)) {
      doc.computedNodes.setComputed(leftNode.parent, false);
    }
  }

  let chain = tiptap.chain();

  sortBy(rangesToDelete, [(r) => -r.from]).forEach(({ from, to }) => {
    chain = chain.deleteRange({ from, to });
  });

  chain.run();

  queueMicrotask(() => {
    logger.debug("Move cursor after merge");
    if (moveToNode) {
      doc.cursor.moveTo(...moveToNode);
    } else {
      jumpToNearbySiblingNode(doc);
    }
    mergeInlineEditables(doc);
  });

  return focusPos;
}

export function findAndMergeExpressionNodes(transaction: Transaction, doc: DocumentModel) {
  let minPos = Infinity;
  let maxPos = -Infinity;

  transaction.mapping.maps.forEach((map) => {
    map.forEach((_, __, newStart, newEnd) => {
      minPos = Math.min(minPos, newStart);
      maxPos = Math.max(maxPos, newEnd);
    });
  });

  let range: Range = { from: 0, to: transaction.doc.content.size };

  if (minPos !== Infinity) {
    range = {
      from: Math.max(0, minPos - 1),
      to: Math.min(transaction.doc.content.size, maxPos + 1),
    };
  }

  const nodesToMerge = findExpressionNodesToMerge(transaction, doc, range);

  if (nodesToMerge.length > 0) {
    doc.transact(() => {
      const focusPos = mergeExpressionNodes(nodesToMerge, doc, transaction);

      if (focusPos !== -1) {
        doc.editor.tiptap?.commands.setNodeSelection(focusPos);
      }
    }, "Tiptap:mergeExpressionNodes");
  }
}
